/**
 * Validação alimentar das sugestões de refeição (pura, sem IO).
 *
 * Regras vinculativas:
 * - um cartão de almoço/jantar só pode usar um candidato com sinais reais de
 *   comida (restaurante, café, pastelaria, bar, padaria…);
 * - uma atração (museu, miradouro, observatório, praça, igreja, monumento)
 *   nunca vira restaurante por proximidade;
 * - por defeito, o mesmo `placeId` não se repete em almoço e jantar do mesmo
 *   dia;
 * - refeições continuam blocos separados e não contam como visitas.
 */

const FOOD_TYPE_RE =
  /restaurant|cafe|bar\b|bakery|food|meal_|coffee|pub|brewery|winery|ice_cream|patisserie|deli/;

const NON_FOOD_TYPE_RE =
  /museum|tourist_attraction|observation_deck|park|church|place_of_worship|monument|art_gallery|zoo|aquarium|stadium|landmark|historical|viewpoint|library|city_hall/;

const NON_FOOD_NAME_RE =
  /\b(observatory|observation\s+deck|museu|museum|miradouro|viewpoint|mirador|catedral|cathedral|igreja|church|palace|pal[aá]cio|monument|memorial|castle|castelo|jardim|garden|park|torre|tower|gallery|galeria)\b/i;

/**
 * Sinais positivos no próprio nome. Sem estes, um restaurante legítimo como
 * "Bryant Park Grill" seria rejeitado só por conter "Park" — foi essa a causa
 * dos blocos de refeição sem sugestão.
 */
const FOOD_NAME_RE =
  /\b(restaurante?|restaurant|ristorante|marisqueira|cervejaria|tasca|taberna|tavern|trattoria|osteria|brasserie|bistro|bistr[oô]|grill|grelhados?|churrasqueira|steak\s*house|steakhouse|bar|pub|caf[ée]|cafe|coffee|cafeteria|pastelaria|patisserie|p[âa]tisserie|padaria|bakery|boulangerie|confeitaria|doceria|geladaria|gelateria|sorveteria|pizzeria|pizzaria|sushi|ramen|taqueria|cantina|canteen|kitchen|cozinha|eatery|diner|deli|delicatessen|snack|food\s*hall|mercado\s+gourmet|wine\s*bar|enoteca|adega|petiscos?|tapas|churrascaria|hamburgueria|burger|noodle|dim\s*sum|bibimbap|creperia|cr[êe]perie)\b/i;

export type MealCandidate = {
  placeId?: string;
  name?: string;
  types?: string[];
  primaryType?: string;
  /** Cozinha declarada pelo fornecedor ("Japonês", "Italian"…). */
  cuisine?: string;
};

/** O candidato tem sinais reais de local de comida? */
export function isFoodCandidate(candidate: MealCandidate | null | undefined): boolean {
  if (!candidate) return false;
  const types = (candidate.types ?? []).map((t) => t.toLowerCase());
  const primary = (candidate.primaryType ?? "").toLowerCase();
  const all = primary ? [...types, primary] : types;
  if (all.length > 0) {
    if (!all.some((t) => FOOD_TYPE_RE.test(t))) return false;
    if (primary && NON_FOOD_TYPE_RE.test(primary) && !FOOD_TYPE_RE.test(primary)) return false;
    return true;
  }
  const name = candidate.name ?? "";
  // Sinal alimentar explícito no nome ou na cozinha vence o nome ambíguo.
  if (FOOD_NAME_RE.test(name)) return true;
  if ((candidate.cuisine ?? "").trim().length > 0) return true;
  // Sem metadados de tipo: só o nome pode desqualificar; nunca qualificar.
  return !NON_FOOD_NAME_RE.test(name);
}


/**
 * Escolhe a sugestão para um bloco de refeição, rejeitando candidatos não
 * alimentares e repetições do mesmo `placeId` no mesmo dia.
 */
export function pickMealSuggestion<T extends MealCandidate>(
  candidates: readonly T[],
  usedPlaceIdsSameDay: Iterable<string>,
  opts: { allowRepeat?: boolean } = {},
): T | null {
  const used = new Set(usedPlaceIdsSameDay);
  const eligible = candidates.filter((c) => isFoodCandidate(c));
  if (eligible.length === 0) return null;
  const fresh = eligible.find((c) => !c.placeId || !used.has(c.placeId));
  if (fresh) return fresh;
  // Sem alternativas válidas: só repete quando explicitamente permitido.
  return opts.allowRepeat ? eligible[0] : null;
}
